import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MapPin,
  Mail,
  ExternalLink,
  Download,
  Award,
  Users,
  Calendar,
  BookOpen,
  Code,
  Database,
  Cloud
} from 'lucide-react';
import { profileData, skills, journey } from '../data/mock';
import { useData } from '@/contexts/DataContext';

const About = () => {

  const { testimonials } = useData()

  const stats = [
    { label: 'Years Experience', value: '5+', icon: Calendar },
    { label: 'Projects Completed', value: '30+', icon: Code },
    { label: 'Happy Clients', value: '60+', icon: Users },
    { label: 'Technologies', value: '20+', icon: Award },
  ];

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-5xl font-bold">
                  About{' '}
                  <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
                    Me
                  </span>
                </h1>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  I'm a passionate Full Stack Developer and Data Engineer who loves turning complex problems into elegant solutions.
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  {profileData.bio} Currently working at Regtify, a Cyprus-based fintech company, where I develop scalable financial technology solutions.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  My journey spans from leading development teams at Techxudo to mastering data engineering at Xnitia using AWS and PySpark. I'm passionate about sharing knowledge through my Medium blog and continuously learning new technologies.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-purple-600 hover:bg-purple-700">
                  <Link to="/contact">
                    Get In Touch
                    <Mail className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href={`https://drive.google.com/file/d/1njo7UH7KvynYqkkzoQ4KZ3WAhka6h_LN/view?usp=sharing`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download Resume
                  </a>
                </Button>
              </div>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{profileData.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${profileData.email}`} className="hover:text-purple-600 transition-colors">
                    {profileData.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Right Content - Profile Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="w-96 h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/20 dark:to-purple-800/20 profile-img">
                  <img
                    src={profileData.profileImage}
                    alt={profileData.name}
                    className="w-full h-full object-cover profile-img"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white dark:bg-card rounded-2xl p-4 shadow-lg border">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">5+</div>
                    <div className="text-sm text-muted-foreground">Years Experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="mb-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
                <CardContent className="p-0 space-y-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/20">
                    <stat.icon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-600">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Skills Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Technical Expertise</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive skill set across multiple domains and technologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skills.map((skillGroup, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow border-l-4 border-l-purple-600">
                <CardContent className="p-0">
                  <h3 className="text-xl font-semibold mb-4 text-purple-700 dark:text-purple-300">
                    {skillGroup.category}
                  </h3>
                  <div className="space-y-2">
                    {skillGroup.technologies.map((tech, techIndex) => (
                      <div key={techIndex} className="flex items-center justify-between">
                        <span className="text-sm">{tech}</span>
                        <div className="w-20 h-2 bg-muted rounded-full">
                          <div
                            className="h-full bg-gradient-to-r from-purple-600 to-purple-700 rounded-full"
                            style={{ width: `${Math.random() * 30 + 70}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Journey Timeline */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">My Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Key milestones in my professional development and career growth
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-gradient-to-b from-purple-600 to-purple-200 hidden lg:block"></div>

            <div className="space-y-12">
              {journey?.map((item, index) => (
                <div key={index} className="relative">
                  <div className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 0 ? '' : 'lg:text-right'}`}>
                    {/* Timeline Dot */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-purple-600 rounded-full border-4 border-background z-10 hidden lg:block"></div>

                    {/* Content */}
                    <div className={`${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12 lg:col-start-2'}`}>
                      <Card className="p-6 hover:shadow-lg transition-shadow">
                        <CardContent className="p-0">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/20">
                              <item.icon className="h-5 w-5 text-purple-600" />
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {item.year}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                          <p className="text-muted-foreground">{item.description}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">What Colleagues Say</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Feedback from team members and collaborators I've worked with
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial._id} className="p-6 hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="flex items-start gap-4">
                    <img
                      src={testimonial.images?.url}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-muted-foreground mb-4 italic">
                        "{testimonial.message}"
                      </p>
                      <div>
                        <div className="font-semibold">{testimonial.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {testimonial.position} at {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Links Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Connect With Me</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find me on various platforms where I share insights and connect with the community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/20">
                  <ExternalLink className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">LinkedIn</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Professional networking and career updates
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href={profileData.social.linkedin} target="_blank" rel="noopener noreferrer">
                      Connect
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/20">
                  <BookOpen className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Medium Blog</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Technical articles and tutorials
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href={profileData.social.medium} target="_blank" rel="noopener noreferrer">
                      Follow
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow md:col-span-2 lg:col-span-1">
              <CardContent className="p-0 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/20">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Email</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Direct communication for opportunities
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`mailto:${profileData.email}`}>
                      Send Email
                      <Mail className="ml-2 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-purple-50 to-black/5 dark:from-purple-950/20 dark:to-black/40 rounded-3xl p-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Let's Work Together
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            I'm always interested in new opportunities and exciting projects. Whether you need a full-stack developer or data engineering expertise, let's discuss how we can collaborate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-purple-600 hover:bg-purple-700">
              <Link to="/contact">
                Start a Conversation
                <Mail className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/portfolio">
                View My Work
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;